/**
 * ControlActivitiesSection
 * 
 * Search result section for Control Activities (EX5)
 * Displays matching control activities in a table
 * Matches exact styling from ControlActivitiesPage.tsx
 * Includes expandable testing columns
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tooltip } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { ControlActivity } from '../../../../services/controlActivitiesService';
import HighlightText from '../components/HighlightText';

// ========================================
// Styled Components - Match ControlActivitiesPage.tsx exactly
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #D1FAE5;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #A7F3D0;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #065F46;
  margin: 0;
`;

const ExpandAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #A7F3D0;
  border-radius: 6px;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  color: #065F46;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #ECFDF5;
  }
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    white-space: nowrap;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 12px;
    color: #1F2937;
    padding: 12px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    vertical-align: middle;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

// Chip Components - Match ControlActivitiesPage.tsx exactly
const StatusChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'Active': return '#4AB7B6';
      case 'Draft': return '#E5B454';
      default: return '#F3F4F6';
    }
  }};
  color: white;
  white-space: nowrap;
  
  &::before {
    content: '●';
    font-size: 8px;
  }
`;

const ControlIdChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CycleChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #ECFDF5;
  color: #047857;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProcessChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const GrayChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: white;
  color: #374151;
  border: 1px solid #E5E7EB;
  white-space: nowrap;
`;

const RiskRankingChip = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
  background: white;
  white-space: nowrap;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 8px;
  }
`;

const TestingScopeChip = styled.span<{ $inScope: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: white;
  color: ${props => props.$inScope ? '#2DD4BF' : '#9CA3AF'};
  border: 1px solid ${props => props.$inScope ? '#2DD4BF' : '#E5E7EB'};
  white-space: nowrap;
`;

const DesignEffectiveChip = styled.span<{ $value: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props => {
    switch (props.$value) {
      case 'Yes': return '#DCFCE7';
      case 'No': return '#FEE2E2';
      default: return '#F3F4F6';
    }
  }};
  color: ${props => {
    switch (props.$value) {
      case 'Yes': return '#16A34A';
      case 'No': return '#DC2626';
      default: return '#6B7280';
    }
  }};
`;

const CompletionChip = styled.span<{ $value: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props => {
    switch (props.$value) {
      case 'Completed':
      case 'Yes': return '#DCFCE7';
      case 'WIP': return '#FEF3C7';
      case 'Not Started':
      case 'No': return '#FEE2E2';
      default: return '#F3F4F6';
    }
  }};
  color: ${props => {
    switch (props.$value) {
      case 'Completed':
      case 'Yes': return '#16A34A';
      case 'WIP': return '#D97706';
      case 'Not Started':
      case 'No': return '#DC2626';
      default: return '#6B7280';
    }
  }};
`;

const DescriptionText = styled.div`
  font-size: 12px;
  color: #1F2937;
  
  .what-label {
    font-weight: 600;
    color: #1F2937;
  }
  
  .what-text {
    font-weight: 400;
    color: #6B7280;
  }
`;

const EmptyCell = styled.span`
  color: #9CA3AF;
  font-size: 12px;
`;

// ========================================
// Types
// ========================================

export interface ControlActivitiesSectionProps {
  data: ControlActivity[];
  searchText: string;
}

// ========================================
// Component
// ========================================

const ControlActivitiesSection: React.FC<ControlActivitiesSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();
  const [showTestingColumns, setShowTestingColumns] = useState(false);

  // Navigation handlers
  const handleNavigateToControl = (controlId: string) => {
    navigate(`/control-activities/${controlId}`);
  };

  const handleNavigateToCycle = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const handleNavigateToProcess = (processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  // Main columns (always visible)
  const mainColumns: ColumnsType<ControlActivity> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <StatusChip $status={status}>{status}</StatusChip>
      ),
    },
    {
      title: 'Control ID',
      dataIndex: 'controlCode',
      key: 'controlCode',
      width: 100,
      render: (code: string, record) => (
        <ControlIdChip onClick={() => handleNavigateToControl(record.id)}>
          <HighlightText text={code || '-'} searchText={searchText} />
        </ControlIdChip>
      ),
    },
    {
      title: 'Cycle',
      key: 'cycle',
      width: 120,
      render: (_, record) => {
        const cycleText = `${record.cycle?.code || ''} ${record.cycle?.name || ''}`.trim();
        return (
          <CycleChip onClick={() => handleNavigateToCycle(record.cycle?.id || '')}>
            <HighlightText text={cycleText || '-'} searchText={searchText} />
          </CycleChip>
        );
      },
    },
    {
      title: 'Process',
      key: 'process',
      width: 150,
      render: (_, record) => {
        const processText = `${record.process?.code || ''} ${record.process?.name || ''}`.trim();
        return (
          <Tooltip title={processText}>
            <ProcessChip onClick={() => handleNavigateToProcess(record.process?.id || '')}>
              <HighlightText text={processText || '-'} searchText={searchText} />
            </ProcessChip>
          </Tooltip>
        );
      },
    },
    {
      title: 'Control Description',
      key: 'controlDescription',
      width: 200,
      render: (_, record) => (
        <DescriptionText>
          <span className="what-label">What:</span>{' '}
          <span className="what-text">
            <HighlightText text={record.controlDescription?.what || '-'} searchText={searchText} />
          </span>
        </DescriptionText>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'controlCategory',
      key: 'controlCategory',
      width: 100,
      render: (category: string) => (
        <GrayChip>{category || '-'}</GrayChip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'controlType',
      key: 'controlType',
      width: 100,
      render: (type: string) => (
        <GrayChip>{type || '-'}</GrayChip>
      ),
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 90,
      render: (freq: string) => (
        <GrayChip>{freq || '-'}</GrayChip>
      ),
    },
    {
      title: 'Key/Non-Key',
      dataIndex: 'keyOrNonKey',
      key: 'keyOrNonKey',
      width: 100,
      render: (value: string) => (
        <GrayChip>{value || '-'}</GrayChip>
      ),
    },
    {
      title: 'Risk Ranking',
      dataIndex: 'riskRanking',
      key: 'riskRanking',
      width: 110,
      render: (riskRanking: string) => (
        <RiskRankingChip $level={riskRanking}>
          {riskRanking || 'Unranked'}
        </RiskRankingChip>
      ),
    },
    {
      title: 'Testing Scope',
      dataIndex: 'testingScope',
      key: 'testingScope',
      width: 110,
      render: (testingScope: boolean) => (
        <TestingScopeChip $inScope={testingScope}>
          {testingScope ? 'In Scope' : 'Out of Scope'}
        </TestingScopeChip>
      ),
    },
  ];

  // Testing columns (only visible when expanded)
  const testingColumns: ColumnsType<ControlActivity> = [
    {
      title: 'Design Effective',
      dataIndex: ['testing', 'designEffective'],
      key: 'designEffective',
      width: 120,
      render: (value: string, record) => {
        if (!record.testingScope) return <EmptyCell>-</EmptyCell>;
        return <DesignEffectiveChip $value={value || 'N/A'}>{value || 'N/A'}</DesignEffectiveChip>;
      },
    },
    {
      title: 'Total Samples',
      dataIndex: ['testing', 'totalTestSamples'],
      key: 'totalTestSamples',
      width: 100,
      align: 'center',
      render: (value: number, record) => {
        if (!record.testingScope) return <EmptyCell>-</EmptyCell>;
        return value ?? 0;
      },
    },
    {
      title: 'Samples Tested',
      dataIndex: ['testing', 'samplesTested'],
      key: 'samplesTested',
      width: 110,
      align: 'center',
      render: (value: number, record) => {
        if (!record.testingScope) return <EmptyCell>-</EmptyCell>;
        return value ?? 0;
      },
    },
    {
      title: 'Testing Status',
      key: 'testingStatus',
      width: 100,
      align: 'center',
      render: (_, record) => {
        if (!record.testingScope || !record.testing) return <EmptyCell>-</EmptyCell>;
        const total = record.testing.totalTestSamples ?? 0;
        const tested = record.testing.samplesTested ?? 0;
        if (total === 0) return 'N/A';
        const percentage = ((tested / total) * 100).toFixed(2);
        return `${percentage}%`;
      },
    },
    {
      title: 'Interim',
      dataIndex: ['testing', 'interimCompleted'],
      key: 'interimCompleted',
      width: 100,
      render: (value: string, record) => {
        if (!record.testingScope) return <EmptyCell>-</EmptyCell>;
        return <CompletionChip $value={value || 'N/A'}>{value || 'N/A'}</CompletionChip>;
      },
    },
    {
      title: 'Final',
      key: 'finalCompleted',
      width: 100,
      render: (_, record) => {
        if (!record.testingScope || !record.testing) return <EmptyCell>-</EmptyCell>;
        const total = record.testing.totalTestSamples ?? 0;
        const tested = record.testing.samplesTested ?? 0;
        if (total === 0 && tested === 0) return <CompletionChip $value="N/A">N/A</CompletionChip>;
        if (total === 0) return <CompletionChip $value="N/A">N/A</CompletionChip>;
        const testingStatus = Math.round((tested / total) * 100);
        let status: string;
        if (testingStatus === 0) status = 'Not Started';
        else if (testingStatus < 100) status = 'WIP';
        else status = 'Completed';
        return <CompletionChip $value={status}>{status}</CompletionChip>;
      },
    },
    {
      title: 'Operating Effective',
      dataIndex: ['testing', 'operatingEffectiveness'],
      key: 'operatingEffectiveness',
      width: 130,
      render: (value: string, record) => {
        if (!record.testingScope) return <EmptyCell>-</EmptyCell>;
        return <DesignEffectiveChip $value={value || 'N/A'}>{value || 'N/A'}</DesignEffectiveChip>;
      },
    },
  ];

  // Combine columns based on expanded state
  const columns = useMemo(() => {
    if (showTestingColumns) {
      return [...mainColumns, ...testingColumns];
    }
    return mainColumns;
  }, [showTestingColumns, searchText]);

  // Don't render if no results
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Control Activities ({data.length} results)</SectionTitle>
        <ExpandAllButton onClick={() => setShowTestingColumns(!showTestingColumns)}>
          {showTestingColumns ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          {showTestingColumns ? 'Hide Testing' : 'Show Testing'}
        </ExpandAllButton>
      </SectionHeader>
      
      <ContentContainer>
        <TableWrapper>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={data.length > 10 ? { pageSize: 10 } : false}
            size="small"
            scroll={{ x: showTestingColumns ? 1800 : 1200 }}
          />
        </TableWrapper>
      </ContentContainer>
    </SectionContainer>
  );
};

export default ControlActivitiesSection;
